import React, { useState, useRef, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { UserProfile, Material, FinancialConfig, ProductTemplate, SimpleService } from '../types';
import { 
  ArrowLeft, 
  Package, 
  Send, 
  Camera, 
  CheckCircle2,
  Loader2,
  ChevronDown,
  ChevronRight,
  Zap,
  Info,
  Plus,
  X,
  ScanLine,
  Cloud,
  ClipboardList,
  Briefcase,
  Home,
  PlayCircle,
  ImageIcon,
  FileText,
  Search,
  AlertTriangle,
  ShoppingBag,
  Tag,
  Barcode,
  Truck,
  ArrowDownRight
} from 'lucide-react';
import RecipeManager from './RecipeManager';
import ServiceManager from './ServiceManager';
import ProductCatalogManager from './ProductCatalogManager';
import { motion, AnimatePresence } from 'motion/react';
import { formatCurrencyBRL, parseCurrencyMask } from '../utils/formatUtils';
import { TUTORIAL_LINKS } from '../constants';
import { generateContentWithCache } from '../lib/geminiService';
import { supabase } from '../lib/supabase';
import { compressImage } from '../utils/imageUtils';
import { decodeQRCodeFromFile } from '../utils/qrCodeUtils';
import { parseNFCeQRCode, buildQRContext } from '../utils/nfceParser';
import { parseAIJSON } from '../utils/aiUtils';
import PaywallModal from './PaywallModal';

import { useCostCenterTabs, CostCenterTab } from '../hooks/useCostCenterTabs';
import { useMaterialEditor } from '../hooks/useMaterialEditor';
import { getLocalDateString } from '../modules/erp/utils/calendarUtils';
import { useNicheTemplates } from '../hooks/useNicheTemplates';
import { useCostCenterAI, ProcessedItem } from '../hooks/useCostCenterAI';
import { hasStagedServices, hasStagedRecipe, BRIDGE_EVENTS } from '../modules/erp/utils/commandBridge';
import { useHardwareScanner } from '../modules/erp/ind/hooks/useHardwareScanner';
import { isPhysicalStockMaterial, isFixedExpenseMaterial, resolveStockControlMode } from '../modules/erp/utils/stockItemUtils';

interface Props {
  user: UserProfile;
  config: FinancialConfig;
  materials: Material[];
  recipes: ProductTemplate[];
  services: SimpleService[];
  onSaveMaterials: (materials: Material[]) => void;
  onSaveRecipe: (recipe: ProductTemplate) => void;
  onDeleteRecipe: (id: string) => void;
  onSaveService: (service: SimpleService) => void;
  onDeleteService: (id: string) => void;
  onBack: () => void;
  initialTab?: CostCenterTab;
  onNavigateToStock?: () => void;
}


const CurrencyInput = ({ value, onChange, className, placeholder }: { value: number, onChange: (val: number) => void, className?: string, placeholder?: string }) => {
  const [displayValue, setDisplayValue] = useState(() => {
    return (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  });

  React.useEffect(() => {
    const parentVal = (value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (parentVal !== displayValue && Math.abs(parseCurrencyMask(displayValue) - value) > 0.001) {
      setDisplayValue(parentVal);
    }
  }, [value, displayValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let digits = e.target.value.replace(/\D/g, '');
    if (!digits) digits = '0';
    const numericValue = parseInt(digits, 10) / 100;
    
    setDisplayValue(numericValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    onChange(numericValue);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      className={className}
      placeholder={placeholder}
    />
  );
};

const PaywallEmptyState = ({ title, description, onUpgrade }: { title: string, description: string, onUpgrade: () => void }) => (
  <div className="flex flex-col items-center justify-center py-20 px-6 text-center bg-white rounded-3xl border border-zinc-100 shadow-sm mx-4 mt-6">
    <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mb-6">
      <Zap className="h-8 w-8 text-amber-500" />
    </div>
    <h3 className="text-xl font-black text-zinc-900 uppercase tracking-widest mb-3">{title}</h3>
    <p className="text-sm text-zinc-500 max-w-sm mb-8 leading-relaxed">
      {description}
    </p>
    <button 
      onClick={onUpgrade}
      className="py-3 px-8 bg-zinc-900 text-white font-bold uppercase tracking-widest text-xs rounded-2xl hover:bg-zinc-800 transition-colors shadow-lg shadow-zinc-900/20 cursor-pointer active:scale-95"
    >
      Desbloquear Plano PRO
    </button>
  </div>
);


export default function CostCenter({ 
  user, 
  config, 
  materials, 
  recipes, 
  services, 
  onSaveMaterials, 
  onSaveRecipe, 
  onDeleteRecipe, 
  onSaveService, 
  onDeleteService, 
  onBack,
  initialTab = 'materials',
  onNavigateToStock
}: Props) {
  const { mainTab, changeTab } = useCostCenterTabs(initialTab);

  // Ponte Omnibar -> Catálogo de Serviços: abre a aba Serviços quando há serviços aguardando validação
  React.useEffect(() => {
    const openServicesTab = () => {
      if (hasStagedServices(user.id)) changeTab('services');
    };
    openServicesTab();
    window.addEventListener(BRIDGE_EVENTS.services, openServicesTab);
    return () => window.removeEventListener(BRIDGE_EVENTS.services, openServicesTab);
  }, [user.id, changeTab]);

  // Ponte Omnibar -> Fichas Técnicas / Receitas: abre a aba Receitas quando há receita aguardando validação
  React.useEffect(() => {
    const openRecipesTab = () => {
      if (hasStagedRecipe(user.id)) changeTab('recipes');
    };
    openRecipesTab();
    window.addEventListener(BRIDGE_EVENTS.recipes, openRecipesTab);
    return () => window.removeEventListener(BRIDGE_EVENTS.recipes, openRecipesTab);
  }, [user.id, changeTab]);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'pot_open' | 'unit_sale'>('all');
  const [fixedSearchTerm, setFixedSearchTerm] = useState('');
  const [pendingConversions, setPendingConversions] = useState<any[]>([]);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);

  // Estados da Conversão Manual
  const [manualConversionIdx, setManualConversionIdx] = useState<number | null>(null);
  const [manualUnit, setManualUnit] = useState<string>('m');
  const [manualCustomUnit, setManualCustomUnit] = useState<string>('');
  const [manualYield, setManualYield] = useState<string>('');

  const physicalMaterials = useMemo(() => materials.filter(m => isPhysicalStockMaterial(m)), [materials]);
  const fixedExpenseMaterials = useMemo(() => materials.filter(m => isFixedExpenseMaterial(m)), [materials]);

  const filteredMaterials = useMemo(() => {
    return physicalMaterials.filter(m => {
      const term = searchTerm.toLowerCase().trim();
      const matchesSearch = !term || 
        m.name.toLowerCase().includes(term) || 
        (m.brand && m.brand.toLowerCase().includes(term)) ||
        (m.barcode && m.barcode.toLowerCase().includes(term)) ||
        (m.supplierName && m.supplierName.toLowerCase().includes(term));
      const matchesCategory = categoryFilter === 'all' || resolveStockControlMode(m) === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [physicalMaterials, searchTerm, categoryFilter]);

  const filteredFixedExpenses = useMemo(() => {
    const term = fixedSearchTerm.toLowerCase().trim();
    return fixedExpenseMaterials.filter(m => {
      if (!term) return true;
      return m.name.toLowerCase().includes(term) || (m.brand && m.brand.toLowerCase().includes(term));
    });
  }, [fixedExpenseMaterials, fixedSearchTerm]);

  const counts = useMemo(() => {
    const total = physicalMaterials.length;
    const potOpen = physicalMaterials.filter(m => resolveStockControlMode(m) === 'pot_open').length;
    const unitSale = physicalMaterials.filter(m => resolveStockControlMode(m) === 'unit_sale').length;
    const fixo = fixedExpenseMaterials.length;
    return { total, variavel: total, potOpen, unitSale, fixo };
  }, [physicalMaterials, fixedExpenseMaterials]);

  const {
    suggestions,
    allTemplates,
    isLoadingSuggestions,
    dismissSuggestion
  } = useNicheTemplates({
    userNiche: user.niche || '',
    existingMaterials: materials,
    userId: user.id
  });

  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const isShrunk = false;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: materials.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 92,
    overscan: 5,
  });

  // Bugfix de Filtragem/Aba: Reseta scroll ao mudar de aba ou modificar o tamanho do inventário
  React.useEffect(() => {
    if (mainTab === 'materials') {
      parentRef.current?.scrollTo(0, 0);
    }
  }, [mainTab, materials.length]);

  const handleSyncMaterials = React.useCallback(async (updatedMaterials: Material[]) => {
    setIsSyncing(true);
    const hardenedMaterials = updatedMaterials.map(m => ({
      ...m,
      id: m.id || crypto.randomUUID()
    }));
    
    try {
      await onSaveMaterials(hardenedMaterials);
      setLastSaved(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
    } catch (err) {
      console.error('Material sync error:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [onSaveMaterials]);

  const {
    isEditModalOpen,
    setIsEditModalOpen,
    editingMaterial,
    setEditingMaterial,
    pendingDeleteMaterial,
    linkedRecipesForDelete,
    handleCreateNew,
    handleEditOpen,
    handleSaveMaterial: handleUpdateMaterial,
    handleDeleteMaterial: handleRemoveMaterial,
    confirmDeleteMaterial,
    cancelDeleteMaterial,
  } = useMaterialEditor({
    userId: user.id,
    materials,
    recipes,
    onSyncMaterials: handleSyncMaterials,
  });

  const handleCreateNewFixedExpense = React.useCallback(() => {
    setEditingMaterial({
      userId: user.id,
      name: '',
      category: 'fixo',
      itemType: 'custo_fixo',
      unit: 'mês',
      pricePaid: 0,
      yieldAmount: 1,
    });
    setIsEditModalOpen(true);
  }, [user.id, setEditingMaterial, setIsEditModalOpen]);

  const handleQuickDepleteMaterial = React.useCallback((m: Material, delta: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const currentStock = Number(m.stockQuantity || 0);
    const nextStock = Math.max(0, Number((currentStock + delta).toFixed(3)));
    const updated = materials.map(item => item.id === m.id ? { ...item, stockQuantity: nextStock } : item);
    handleSyncMaterials(updated);
  }, [materials, handleSyncMaterials]);

  useHardwareScanner({
    enabled: isEditModalOpen || mainTab === 'materials',
    onScan: (parsedData) => {
      const cleanCode = (parsedData.gtin || parsedData.raw || '').trim();
      if (!cleanCode) return;
      if (isEditModalOpen && editingMaterial && editingMaterial.category !== 'fixo') {
        setEditingMaterial(prev => prev ? { ...prev, barcode: cleanCode } : prev);
        return;
      }
      if (!isEditModalOpen && mainTab === 'materials') {
        const found = physicalMaterials.find(m => m.barcode?.trim().toLowerCase() === cleanCode.toLowerCase());
        if (found) {
          handleEditOpen(found);
        } else {
          setEditingMaterial({
            userId: user.id,
            name: '',
            category: 'variável',
            itemType: 'insumo',
            unit: 'pote',
            pricePaid: 0,
            yieldAmount: 1,
            barcode: cleanCode,
            stockControlMode: 'pot_open',
            stockQuantity: 1,
          });
          setIsEditModalOpen(true);
        }
      }
    },
    maxKeystrokeIntervalMs: 45
  });

  // Preenche a sugestão selecionada no modal de edição para o usuário informar o valor antes de salvar
  const handleQuickAddDirect = React.useCallback((s: { id: string; name: string; category: string; unit: string }) => {
    setEditingMaterial({
      userId: user.id,
      name: s.name,
      category: s.category.toLowerCase().includes('fixo') ? 'fixo' : 'variável',
      unit: s.unit || 'un',
      pricePaid: 0,
      yieldAmount: 1,
    });
    setIsEditModalOpen(true);
  }, [user.id, setEditingMaterial, setIsEditModalOpen]);
  const handleConfirmAIItem = React.useCallback(async (item: Omit<ProcessedItem, 'id'>) => {
    const material: Material = {
      id: crypto.randomUUID(),
      userId: user.id,
      name: item.name,
      category: item.category,
      unit: item.unit,
      pricePaid: item.price,
      yieldAmount: item.quantity,
      depreciationMonths: item.depreciationMonths
    };
    await handleSyncMaterials([...materials, material]);
  }, [materials, user.id, handleSyncMaterials]);

  const handleUpdateExistingMaterialPrice = React.useCallback(async (existingId: string, newPrice: number, newName?: string) => {
    const updatedMaterials = materials.map(m => {
      if (m.id === existingId) {
        return {
          ...m,
          pricePaid: newPrice,
          price: newPrice,
          ...(newName ? { name: newName } : {})
        };
      }
      return m;
    });

    await handleSyncMaterials(updatedMaterials);

    const targetMaterial = materials.find(m => m.id === existingId);
    const materialName = newName || targetMaterial?.name || 'Insumo';

    try {
      const isValidUUID = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      await supabase.rpc('record_user_item_price_history', {
        p_material_name: materialName,
        p_price: newPrice,
        p_unit: targetMaterial?.unit || 'un',
        p_material_id: existingId && isValidUUID(existingId) ? existingId : null,
        p_source_type: 'pdf_ocr'
      });
    } catch (err) {
      console.error('[CostCenter] Erro ao gravar histórico de preço:', err);
    }
  }, [materials, handleSyncMaterials]);

  const {
    inputValue,
    setInputValue,
    isProcessing,
    pendingItems,
    setPendingItems,
    statusMessage: aiStatusMessage,
    setStatusMessage,
    processWithAI,
    updatePendingItem,
    discardItem,
    confirmItem
  } = useCostCenterAI({
    userId: user.id,
    onConfirmItem: handleConfirmAIItem
  });



  const [isCaptureMenuOpen, setIsCaptureMenuOpen] = useState(false);
  const [isDirectProcessing, setIsDirectProcessing] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const pdfInputRef = useRef<HTMLInputElement>(null);

  const handleDirectGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsDirectProcessing(true);

    // --- PROCESSAMENTO DE PDF VIA EDGE FUNCTION COM DEDUPLICAÇÃO DE INVENTÁRIO ---
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      if (file.size > 4.5 * 1024 * 1024) {
        alert('O arquivo PDF é muito grande (máximo 4.5MB para leitura direta). Por favor, tire uma foto da nota fiscal ou envie um PDF menor.');
        setIsDirectProcessing(false);
        return;
      }
      setStatusMessage('Enviando PDF para o R-Max Edge Service...');

      try {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const base64Data = (event.target?.result as string).split(',')[1];
            setStatusMessage('Extraindo insumos e comparando com inventário...');

            const existingMaterialsPayload = materials.slice(0, 25).map(m => ({
              id: m.id,
              name: m.name,
              price: m.pricePaid || 0,
              unit: m.unit
            }));


            let extractedData: any = null;

            let shouldFallback = true;
            try {
              const { data, error } = await supabase.functions.invoke('process-pdf-invoice', {
                body: {
                  pdfBase64: base64Data,
                  filename: file.name,
                  mimeType: 'application/pdf',
                  existing_materials: existingMaterialsPayload
                }
              });

              if (!error && data && data.itens && Array.isArray(data.itens)) {
                extractedData = data;
                shouldFallback = false;
              } else if (data?.error) {
                console.warn('[CostCenter] Edge function error:', data.error);
                shouldFallback = false;
                throw new Error(data.error);
              }
            } catch (edgeErr: any) {
              console.warn('[CostCenter] Edge function invoke error (fallback):', edgeErr);
              if (!shouldFallback) {
                setIsDirectProcessing(false);
                alert(`Erro na leitura do PDF: ${edgeErr.message || "Tente novamente mais tarde."}`);
                return;
              }
            }

            // Fallback direto via Gemini no frontend se a Edge Function não responder E shouldFallback for true
            if (!extractedData && shouldFallback) {
              setStatusMessage('Processando PDF via Inteligência R-Max Direct...');
              const fallbackPrompt = `Você é o motor de extração de dados do R-Max. Analise este PDF e extraia os insumos de custo para o usuário e dados de mercado anônimos.
INSTRUÇÕES E REGRAS RÍGIDAS:
1. BLOQUEIO LGPD & ÉTICA: É ESTREITAMENTE PROIBIDO retornar nomes de pessoas físicas, CPFs, nomes de vendedores ou qualquer dado do comprador.
2. ZERO IDENTIFICAÇÃO DE FORNECEDORES: Ignore completamente o nome da empresa fornecedora, Razão Social, Nome Fantasia ou CNPJ. Não retorne esses dados.
3. DADOS DE MERCADO ANÔNIMOS: Extraia apenas a UF (sigla do estado em 2 letras, ex: SP, RJ, MG) e a Data de emissão (YYYY-MM-DD), se disponíveis. Se não souber a UF, retorne "SP".
4. ITENS DE CUSTO: Extraia cada insumo, produto ou serviço de logística (ex: Frete, Melhor Envio):
   - descricao: Nome legível do material/produto.
   - quantidade: Volume em formato numérico.
   - unidade: Unidade de medida (ex: UN, KG, M, CX, LT, GR, ML).
   - valor_total: Custo final pago por aquele item específico (numérico com ponto decimal).

INVENTÁRIO ATUAL DO USUÁRIO PARA MATCHING DE DEDUPLICAÇÃO:
${JSON.stringify(existingMaterialsPayload)}
Para cada item extraído, compare com a lista acima:
- Se a similaridade for alta (>=85%), defina suggested_match com existing_item_id, existing_item_name, confidence_score (ex: 0.90) e match_type "high".
- Se a similaridade for ambígua/parcial (60% a 84%), defina match_type "ambiguous".
- Se não for similar a nenhum item (<60%), defina match_type "none".

FORMATO DE SAÍDA EXIGIDO:
Retorne APENAS um objeto JSON válido:
{
  "market_data": { "uf": "SP", "data_emissao": "YYYY-MM-DD" },
  "itens": [
    {
      "descricao": "string",
      "quantidade": number,
      "unidade": "string",
      "valor_total": number,
      "suggested_match": {
        "existing_item_id": "string_ou_null",
        "existing_item_name": "string_ou_null",
        "confidence_score": number,
        "match_type": "high"
      }
    }
  ]
}`;

              const parts = [
                { text: fallbackPrompt },
                { inlineData: { data: base64Data, mimeType: "application/pdf" } }
              ];

              const cacheResponse = await generateContentWithCache({
                model: "gemini-3.5-flash-lite",
                contents: [{ parts }],
                config: { generationConfig: { responseMimeType: "application/json" } },
                skipCache: true
              });

              extractedData = parseAIJSON<any>(cacheResponse.text, null);

              // Salvar inteligência B2B em 2º plano mesmo no modo fallback
              if (extractedData?.itens && Array.isArray(extractedData.itens)) {
                const targetUf = (extractedData.market_data?.uf || "SP").toUpperCase().slice(0, 2);
                for (const item of extractedData.itens) {
                  if (item.descricao && Number(item.valor_total) > 0) {
                    const qty = Number(item.quantidade) || 1;
                    await supabase.rpc("submit_market_intelligence_item", {
                      p_item_description: item.descricao,
                      p_unit_price: Number(item.valor_total) / qty,
                      p_total_price: Number(item.valor_total),
                      p_unit: item.unidade || "UN",
                      p_amount: qty,
                      p_uf: targetUf,
                      p_data_source: "pdf_ocr_fallback"
                    }).then(({ error: rpcErr }) => {
                      if (rpcErr) console.error("[CostCenter] Erro RPC Fallback B2B:", rpcErr);
                    });
                  }
                }
              }
            }

            if (!extractedData || !extractedData.itens || !Array.isArray(extractedData.itens) || extractedData.itens.length === 0) {
              throw new Error('Não foi possível identificar insumos legíveis neste PDF. Verifique se o arquivo não está corrompido ou encriptado por senha.');
            }

            const itemsToPending: any[] = [];
            let autoUpdatedCount = 0;

            for (const item of extractedData.itens) {
              const match = item.suggested_match;
              const newPrice = Number(item.valor_total) || 0;

              // Match de Alta Confiabilidade (>= 85%) -> Atualização Automática de Preço no Inventário
              if (match && match.match_type === 'high' && match.existing_item_id) {
                await handleUpdateExistingMaterialPrice(match.existing_item_id, newPrice, match.existing_item_name || item.descricao);
                autoUpdatedCount++;
              } else if (match && match.match_type === 'ambiguous' && match.existing_item_id) {
                // Match Ambíguo (60-84%) -> Fila com Card de Sugestão de Confirmação em 1-Clique
                itemsToPending.push({
                  id: crypto.randomUUID(),
                  name: item.descricao,
                  category: 'variável' as const,
                  unit: item.unidade || 'un',
                  amount: Number(item.quantidade) || 1,
                  price: newPrice,
                  suggestedMatch: {
                    existingItemId: match.existing_item_id,
                    existingItemName: match.existing_item_name,
                    confidenceScore: match.confidence_score
                  }
                });
              } else {
                // Sem Match (< 60%) -> Inserir como Novo Insumo
                itemsToPending.push({
                  id: crypto.randomUUID(),
                  name: item.descricao,
                  category: 'variável' as const,
                  unit: item.unidade || 'un',
                  amount: Number(item.quantidade) || 1,
                  price: newPrice
                });
              }
            }

            if (itemsToPending.length > 0) {
              setPendingItems((prev: any) => [...prev, ...itemsToPending]);
            }

            if (extractedData.pending_conversions && Array.isArray(extractedData.pending_conversions) && extractedData.pending_conversions.length > 0) {
              setPendingConversions((prev: any) => [...prev, ...extractedData.pending_conversions]);
            }

            let msg = `✓ ${extractedData.itens.length} insumos processados com sucesso!`;
            if (autoUpdatedCount > 0) {
              msg += ` (${autoUpdatedCount} preço(s) de insumos atualizados automaticamente).`;
            }
            setStatusMessage(msg);
          } catch (err: any) {
            console.error('Erro na leitura do PDF:', err);
            alert(`Erro na leitura do PDF: ${err.message || 'Falha inesperada'}`);
          } finally {
            setIsDirectProcessing(false);
            setTimeout(() => setStatusMessage(null), 4000);
          }
        };
        reader.readAsDataURL(file);
      } catch (pdfErr: any) {
        console.error('Erro ao ler arquivo PDF:', pdfErr);
        alert('Não foi possível ler o arquivo PDF selecionado.');
        setIsDirectProcessing(false);
        setStatusMessage(null);
      }
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem é muito grande (máximo 5MB). Por favor, reduza a resolução ou tire uma foto mais próxima do cupom fiscal.');
      setIsDirectProcessing(false);
      return;
    }

    setStatusMessage('Comprimindo imagem do cupom...');

    try {
      const qrText = await decodeQRCodeFromFile(file);
      let nfceData = null;
      if (qrText) {
        nfceData = parseNFCeQRCode(qrText);
        if (nfceData.accessKey) {
          supabase.from('nfce_queue')
            .insert({
              access_key: nfceData.accessKey,
              uf: nfceData.uf,
              user_id: user.id
            })
            .then(({ error }) => {
              if (error && error.code !== '23505') {
                console.error('[nfce_queue] Erro ao registrar na fila:', error);
              }
            });
        }
      }

      const compressedDataUrl = await compressImage(file, { purpose: 'ocr' });
      const base64Data = compressedDataUrl.split(',')[1];
      const qrContext = nfceData ? buildQRContext(nfceData) : '';

      setStatusMessage('Enviando para o R-Max identificar os itens...');

      const prompt = `Você é uma inteligência especializada em leitura de cupons fiscais brasileiros.
      Analise a imagem deste cupom fiscal e retorne um JSON estruturado com os dados seguindo EXATAMENTE este formato:
      
      {
        "market_data": {
          "marketName": "Nome do Estabelecimento",
          "location": "Bairro ou Cidade (se disponível)",
          "date": "YYYY-MM-DD",
          "all_items": [
            { "name": "Nome simplificado do produto", "brand": "Marca do produto se explícita, senão null", "price": 0.00, "unit": "un/kg/gr/ml", "originalName": "NOME NO CUPOM" }
          ]
        },
        "inventory_items": [
          { "name": "Nome do material", "brand": "Marca do produto se explícita, senão null", "price": 0.00, "unit": "gr/ml/un/kg", "amount": 500, "category": "variável" }
        ]
      }

      REGRAS CRÍTICAS DE EXTRAÇÃO DE RENDIMENTO:
      1. Para cada item em "inventory_items", você DEVE extrair o RENDIMENTO REAL contido na embalagem (essencial para cálculo de custo).
      2. IGNORE a coluna de quantidade de itens (ex: "2 UN X ...") para o campo "amount"; use o peso/volume que está no NOME do produto (ex: "500GR", "1KG", "900ML").
      3. Se o texto diz "500GR" ou "500G", use unit: "gr" e amount: 500.
      4. Se o texto diz "1LT" ou "1L" ou "900ML", use unit: "ml" e amount: 1000 ou 900.
      5. Se for um item unitário sem peso/volume explícito no nome (ex: "12 OVOS"), use unit: "un" e amount: 12.
      6. O "price" deve ser o valor PAGO por aquela unidade/pacote individual (preço da embalagem).
      7. NUNCA use unidades culinárias (colher, xícara) no cadastro de insumos. Use gr, ml, kg, lt ou un.
      8. AGRUPE itens idênticos em "inventory_items" (ex: se comprou 6 leites, retorne apenas 1 entrada de leite com o preço unitário da caixa).
      9. Simplifique os nomes para que fiquem profissionais (ex: "Manteiga com Sal").
      10. O retorno deve ser EXCLUSIVAMENTE o JSON válido, sem comentários ou markdown.
      11. Somente extraia a marca ('brand') se ela aparecer de forma explícita e literal no texto do cupom. Nunca invente ou alucine marcas para produtos genéricos (como vegetais, carne a granel, ovos avulsos, etc.), retornando null nesses casos.
      12. LIMITE MÁXIMO: Extraia no máximo até 60 itens do cupom fiscal para manter a estabilidade do processamento.
      
      ${qrContext ? `\n\nContexto adicional do QR Code:\n${qrContext}` : ''}`;

      const parts = [
        { text: prompt },
        {
          inlineData: {
            data: base64Data,
            mimeType: "image/jpeg"
          }
        }
      ];

      const cacheResponse = await generateContentWithCache({
        model: "gemini-3.5-flash-lite",
        contents: [{ parts }],
        config: {
          generationConfig: {
            responseMimeType: "application/json"
          }
        },
        skipCache: true
      });

      const data = parseAIJSON<any>(cacheResponse.text, null);
      if (!data || !data.inventory_items) {
        throw new Error('O R-Max não retornou um formato de dados válido. Tente novamente.');
      }

      // Blindagem: Garante teto de até 60 itens por cupom
      if (Array.isArray(data.inventory_items)) {
        data.inventory_items = data.inventory_items.slice(0, 60);
      }
      if (data.market_data?.all_items && Array.isArray(data.market_data.all_items)) {
        data.market_data.all_items = data.market_data.all_items.slice(0, 60);
      }

      if (data.market_data && user?.marketDataSharingConsent === true) {
        try {
          await supabase.from('market_price_intelligence').insert({
            market_name: data.market_data.marketName || 'Desconhecido',
            location: data.market_data.location || 'Brasil',
            date: data.market_data.date || getLocalDateString(),
            items: data.market_data.all_items || [],
            niche: user.niche || 'Geral',
            ...(nfceData?.accessKey && {
              access_key: nfceData.accessKey,
              uf: nfceData.uf,
              data_source: 'qr_context+ocr'
            })
          });
        } catch (dbErr) {
          console.error('Market Intelligence Sync Error:', dbErr);
        }
      }

      const newPendingItems = (data.inventory_items || []).map((item: any) => {
        const itemBrand = item.brand?.trim();
        const itemName = itemBrand ? `${item.name} (${itemBrand})` : item.name;
        return {
          id: crypto.randomUUID(),
          name: itemName,
          category: (item.category === 'fixo' || item.category === 'variável') ? item.category : 'variável',
          unit: item.unit || 'un',
          price: item.price || 0,
          quantity: item.amount || 1,
          isAsset: item.category === 'fixo',
          depreciationMonths: item.depreciationMonths
        };
      });

      if (newPendingItems.length === 0) {
        setStatusMessage('⚠️ Nenhum item de custo ou insumo foi encontrado nesta imagem. Se for uma receita, tente anexar na aba Receitas/Kits.');
      } else {
        setPendingItems((prev) => [...prev, ...newPendingItems]);
        setStatusMessage(`✓ ${newPendingItems.length} insumo(s) identificado(s) com sucesso!`);
      }
      setTimeout(() => setStatusMessage(null), 5000);
    } catch (err: any) {
      console.error('Erro na extração direta do cupom:', err);
      alert(err.message || 'Erro ao processar imagem do cupom.');
      setStatusMessage(null);
    } finally {
      setIsDirectProcessing(false);
    }
  };


  const handleSeedData = () => {
    const seedItems: Material[] = [
      // Custos Fixos
      { id: crypto.randomUUID(), userId: user.id, name: 'Internet e Celular', category: 'fixo', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'MEI (DAS)', category: 'fixo', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Domínio e Hospedagem', category: 'fixo', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Anuidade de Classe', category: 'fixo', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Software e SaaS', category: 'fixo', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Limpeza e Conservação', category: 'fixo', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Segurança', category: 'fixo', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Reserva de Depreciação', category: 'fixo', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      
      // Custos Variáveis Sugeridos
      { id: crypto.randomUUID(), userId: user.id, name: 'Gás', category: 'variável', unit: 'un', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Energia e Água', category: 'variável', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Transporte e Combustível', category: 'variável', unit: 'mês', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Manutenção de Equipamentos', category: 'variável', unit: 'un', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Embalagens e Descartáveis', category: 'variável', unit: 'un', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Educação e Cursos', category: 'variável', unit: 'un', pricePaid: 0, yieldAmount: 1 },
      { id: crypto.randomUUID(), userId: user.id, name: 'Marketing (Ads)', category: 'variável', unit: 'un', pricePaid: 0, yieldAmount: 1 },
    ];
    handleSyncMaterials([...materials, ...seedItems]);
  };

  return (
    <div className="h-dvh flex flex-col overflow-hidden bg-zinc-50 font-sans text-zinc-900">
      {/* Unified Header */}
      <header className="bg-white border-b border-zinc-100 px-4 sm:px-6 py-4 flex items-center justify-between sticky top-0 z-30 shadow-sm shrink-0">
        <div className="flex items-center gap-3 sm:gap-4">
          <button onClick={onBack} className="p-2 hover:bg-zinc-100 rounded-xl transition-colors shrink-0">
            <ArrowLeft className="h-6 w-6 text-zinc-900" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-zinc-900 truncate">Centro de Custos</h1>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <p className="text-[9px] sm:text-[10px] text-zinc-400 font-bold uppercase tracking-widest truncate">Gestão Inteligente</p>
              <div className="h-2 w-px bg-zinc-200 shrink-0" />
              <div className="flex items-center gap-1 shrink-0">
                {isSyncing ? (
                  <>
                    <Loader2 className="h-2.5 w-2.5 text-blue-500 animate-spin" />
                    <span className="text-[9px] text-blue-500 font-bold uppercase tracking-tighter">Salvando...</span>
                  </>
                ) : (
                  <>
                    <Cloud className={`h-2.5 w-2.5 ${lastSaved ? 'text-emerald-500' : 'text-zinc-300'}`} />
                    <span className="text-[9px] text-zinc-400 font-medium tracking-tighter capitalize">
                      {lastSaved ? `Salvo às ${lastSaved}` : 'Nuvem OK'}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {TUTORIAL_LINKS.custos ? (
          <a 
            href={TUTORIAL_LINKS.custos}
            target="_blank"
            rel="noopener noreferrer"
            title="Assistir Vídeo Tutorial desta tela"
            className="p-2 ml-2 bg-blue-50 border border-blue-200 text-blue-600 rounded-xl flex items-center gap-2 hover:bg-blue-100 transition-colors shadow-sm shrink-0 cursor-pointer"
          >
            <PlayCircle className="h-5 w-5" />
            <span className="text-[10px] font-bold uppercase tracking-widest hidden sm:inline">Como Usar</span>
          </a>
        ) : (
          <button 
            title="Em breve: Vídeo Tutorial desta tela"
            className="p-2 ml-2 bg-zinc-100/50 border border-zinc-200/50 rounded-xl flex items-center gap-2 text-zinc-400 opacity-60 cursor-not-allowed shrink-0"
          >
            <PlayCircle className="h-5 w-5" />
            <span className="text-[10px] font-bold uppercase tracking-widest hidden sm:inline">Como Usar</span>
          </button>
        )}
      </header>

      {/* Main Tab Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-zinc-100 sticky top-17.25 z-20 px-4 sm:px-6 py-2 shrink-0">
        <div className="flex gap-2">
          <button
            onClick={() => changeTab('materials')}
            className={`flex-1 py-2.5 px-1 rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all flex flex-col items-center gap-1 ${
              mainTab === 'materials' ? 'bg-amber-50 text-amber-600 shadow-sm' : 'text-zinc-400 hover:bg-zinc-50'
            }`}
          >
            <Package className="h-4 w-4" />
            <span>Insumos & Potes</span>
          </button>
          <button
            onClick={() => changeTab('products')}
            className={`flex-1 py-2.5 px-1 rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all flex flex-col items-center gap-1 ${
              mainTab === 'products' ? 'bg-emerald-50 text-brand shadow-sm' : 'text-zinc-400 hover:bg-zinc-50'
            }`}
          >
            <ShoppingBag className="h-4 w-4" />
            <span>Produtos</span>
          </button>
          <button
            onClick={() => changeTab('recipes')}
            className={`flex-1 py-2.5 px-1 rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all flex flex-col items-center gap-1 ${
              mainTab === 'recipes' ? 'bg-rose-50 text-rose-600 shadow-sm' : 'text-zinc-400 hover:bg-zinc-50'
            }`}
          >
            <ClipboardList className="h-4 w-4" />
            <span>Receitas & Kits</span>
          </button>
          <button
            onClick={() => changeTab('services')}
            className={`flex-1 py-2.5 px-1 rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all flex flex-col items-center gap-1 ${
              mainTab === 'services' ? 'bg-indigo-50 text-indigo-600 shadow-sm' : 'text-zinc-400 hover:bg-zinc-50'
            }`}
          >
            <Briefcase className="h-4 w-4" />
            <span>Serviços</span>
          </button>
          <button
            onClick={() => changeTab('fixed_costs')}
            className={`flex-1 py-2.5 px-1 rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest transition-all flex flex-col items-center gap-1 ${
              mainTab === 'fixed_costs' ? 'bg-amber-100 text-amber-800 shadow-sm' : 'text-zinc-400 hover:bg-zinc-50'
            }`}
          >
            <Home className="h-4 w-4" />
            <span>Despesas & Folha</span>
          </button>
        </div>
      </nav>

      <div className="flex-1 min-h-0 max-w-4xl mx-auto w-full overflow-hidden">
        <AnimatePresence mode="wait">
          {mainTab === 'materials' && (
            <motion.div
              key="materials"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="h-full min-h-0 overflow-y-auto w-full pb-32 px-4 pt-6 space-y-6"
            >
              {/* Extraction Header */}
              <div className="relative z-10">
                <div className={`bg-white border-zinc-100 shadow-lg transition-all duration-500 ${
                  isShrunk 
                    ? 'rounded-none border-b py-2 px-3 bg-white/90 backdrop-blur-md' 
                    : 'rounded-3xl border-2 p-4 sm:p-5 border-b-4 border-b-amber-200'
                }`}>
                  <div className={`flex ${isShrunk ? 'items-center justify-between gap-3' : 'flex-col md:flex-row md:items-center justify-between gap-3'}`}>
                    <div className={`flex items-center gap-2.5 transition-all ${isShrunk ? 'scale-90 origin-left' : ''}`}>
                      <div className="bg-amber-100 p-2 rounded-xl shadow-inner">
                        <Zap className="h-4 w-4 text-amber-600" />
                      </div>
                      {!isShrunk && (
                        <div>
                          <h3 className="font-black text-zinc-900 uppercase text-[11px] tracking-widest leading-none">Entrada Inteligente</h3>
                          <p className="text-[9px] text-zinc-400 font-bold uppercase tracking-tight mt-0.5">Custo Fixo ou Variável</p>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 w-full md:max-w-md relative group">
                      <textarea
                        rows={isShrunk ? 1 : 2}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={isShrunk ? "Extrair novo custo ou anexar..." : "Digite o custo ou anexe a Foto/PDF da nota fiscal..."}
                        className={`w-full bg-zinc-50 border-none rounded-2xl p-3 text-xs sm:text-sm focus:ring-2 focus:ring-amber-500/20 transition-all resize-none overflow-hidden ${
                          isShrunk ? 'h-10 py-2 pr-20' : 'min-h-14 pr-32 pb-2'
                        }`}
                      />
                      <div className={`absolute right-2.5 flex gap-1.5 transition-all ${isShrunk ? 'top-1 scale-75' : 'bottom-2.5'}`}>
                        <input 
                          type="file" 
                          ref={fileInputRef} 
                          onChange={handleDirectGalleryUpload} 
                          accept="image/*,.pdf,application/pdf" 
                          className="hidden" 
                        />
                        <input 
                          type="file" 
                          ref={pdfInputRef} 
                          onChange={handleDirectGalleryUpload} 
                          accept=".pdf,application/pdf" 
                          className="hidden" 
                        />
                        <input 
                          type="file" 
                          ref={cameraInputRef} 
                          onChange={handleDirectGalleryUpload} 
                          accept="image/*" 
                          capture="environment"
                          className="hidden" 
                        />
                        
                        {inputValue && (
                          <button
                            onClick={() => processWithAI(inputValue)}
                            disabled={isProcessing || isDirectProcessing}
                            className="p-2 bg-zinc-900 text-white rounded-xl shadow-md active:scale-95 disabled:opacity-50"
                            title="Extrair com R-Max"
                          >
                            {isProcessing || isDirectProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-amber-500" />}
                          </button>
                        )}

                        <button 
                          onClick={() => setIsCaptureMenuOpen(true)}
                          disabled={isProcessing || isDirectProcessing}
                          className="py-2 px-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl shadow-md shadow-amber-500/20 active:scale-95 transition-all flex items-center gap-1.5 group disabled:opacity-50"
                          title="Anexar Foto ou Documento PDF"
                        >
                          <div className="flex items-center gap-1">
                            <Camera className="h-3.5 w-3.5 text-white" />
                            <span className="text-[9px] opacity-60 font-black text-amber-100">+</span>
                            <FileText className="h-3.5 w-3.5 text-amber-100" />
                          </div>
                          <span className="text-[10px] font-black uppercase tracking-wider hidden sm:inline-block">Anexar Foto / PDF</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  {aiStatusMessage && (
                    <p className="mt-2 text-center text-[10px] font-bold text-amber-700 animate-pulse">
                      {aiStatusMessage}
                    </p>
                  )}
                </div>
              </div>

              {/* Search and Category Filters */}
              <div className="space-y-3 bg-white border border-zinc-100 p-4 rounded-3xl shadow-sm">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Pesquisar insumo, pote, água, picolé, fornecedor ou código de barras..."
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-2xl py-3 pl-11 pr-10 text-xs font-bold text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                  <button
                    onClick={() => setCategoryFilter('all')}
                    className={`py-2 px-3.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all shrink-0 flex items-center gap-1.5 ${
                      categoryFilter === 'all'
                        ? 'bg-amber-500 text-white shadow-sm'
                        : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200/70'
                    }`}
                  >
                    <span>Todos</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${
                      categoryFilter === 'all' ? 'bg-amber-600 text-white' : 'bg-zinc-200 text-zinc-600'
                    }`}>
                      {counts.total}
                    </span>
                  </button>

                  <button
                    onClick={() => setCategoryFilter('pot_open')}
                    className={`py-2 px-3.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all shrink-0 flex items-center gap-1.5 ${
                      categoryFilter === 'pot_open'
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200/70'
                    }`}
                  >
                    <Package className="h-3 w-3" />
                    <span>Potes & Buffet (Baixa ao Abrir)</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${
                      categoryFilter === 'pot_open' ? 'bg-emerald-700 text-white' : 'bg-zinc-200 text-zinc-600'
                    }`}>
                      {counts.potOpen}
                    </span>
                  </button>

                  <button
                    onClick={() => setCategoryFilter('unit_sale')}
                    className={`py-2 px-3.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all shrink-0 flex items-center gap-1.5 ${
                      categoryFilter === 'unit_sale'
                        ? 'bg-amber-700 text-white shadow-sm'
                        : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200/70'
                    }`}
                  >
                    <Package className="h-3 w-3" />
                    <span>Água, Picolés & Unitários</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${
                      categoryFilter === 'unit_sale' ? 'bg-amber-800 text-white' : 'bg-zinc-200 text-zinc-600'
                    }`}>
                      {counts.unitSale}
                    </span>
                  </button>
                </div>
              </div>

              {/* Pendências de Conversão de Unidade (Camada 2 / Fallback) */}
              {pendingConversions.length > 0 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-500">
                  <div className="flex items-center justify-between px-1">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-600 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" /> 
                      Revisão de Unidade de Medida ({pendingConversions.length})
                    </h4>
                  </div>

                  {pendingConversions.map((conv, idx) => (
                    <div key={idx} className="bg-white border-2 border-amber-500/30 rounded-3xl p-6 shadow-xl relative overflow-hidden transition-all">
                      <div className="flex flex-col gap-4">
                        <div className="bg-amber-50/80 border border-amber-200 rounded-2xl p-4 text-xs">
                          <div className="flex items-center gap-2 font-bold text-amber-900 mb-2">
                            <Zap className="h-4 w-4 text-amber-600" />
                            <span>Conversão Inteligente Detectada</span>
                          </div>
                          <p className="text-amber-800 font-medium mb-1">
                            Detectamos que o item <strong>{conv.descricao}</strong> pode ser medido por área.
                          </p>
                          <p className="text-amber-800 font-medium mb-3">
                            Sugerimos a conversão para <strong>{conv.area_m2_detectada.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²</strong> ao preço de <strong>{formatCurrencyBRL(conv.valor_por_m2_sugerido)} / m²</strong>.
                          </p>
                          
                          <div className="flex items-center gap-2 mt-4 mb-4">
                            <input 
                              type="checkbox" 
                              id={`share-conv-${idx}`} 
                              className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                              defaultChecked={false}
                            />
                            <label htmlFor={`share-conv-${idx}`} className="text-xs text-amber-900 cursor-pointer">
                              Compartilhar esta regra com outros profissionais do meu nicho
                            </label>
                          </div>

                          <div className="flex flex-col gap-2">
                            {manualConversionIdx !== idx ? (
                              <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                                <button
                                  onClick={async () => {
                                    const checkbox = document.getElementById(`share-conv-${idx}`) as HTMLInputElement;
                                    const desejaCompartilhar = checkbox?.checked || false;
                                    
                                    try {
                                      const { error } = await supabase.rpc('confirm_unit_conversion_rule', {
                                        p_fornecedor_cnpj: conv.fornecedor_cnpj,
                                        p_fornecedor_nome: conv.fornecedor_nome,
                                        p_codigo_produto: conv.codigo_produto,
                                        p_descricao_snapshot: conv.descricao,
                                        p_unidade_origem: conv.unidade_atual,
                                        p_unidade_convertida: 'm²',
                                        p_area_m2_calculada: conv.area_m2_detectada,
                                        p_fator_conversao: conv.fator_conversao_sugerido,
                                        p_action: 'confirm',
                                        p_deseja_compartilhar: desejaCompartilhar,
                                        p_pattern_id: conv.pattern_id
                                      });
                                      
                                      if (error) throw error;
                                      
                                      setPendingItems((prev: any) => prev.map((item: any) => {
                                        if (item.name === conv.descricao) {
                                          const currentQty = Number(item.amount || item.quantity || 1);
                                          return {
                                            ...item,
                                            unit: 'm²',
                                            amount: currentQty * conv.area_m2_detectada,
                                            quantity: currentQty * conv.area_m2_detectada
                                          };
                                        }
                                        return item;
                                      }));

                                      setPendingConversions(prev => prev.filter((_, i) => i !== idx));
                                      setStatusMessage('Conversão aplicada com sucesso!');
                                      setTimeout(() => setStatusMessage(null), 3000);
                                    } catch (err) {
                                      console.error('Erro ao confirmar conversão:', err);
                                      alert('Erro ao salvar regra de conversão.');
                                    }
                                  }}
                                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2 px-3 rounded-xl font-bold text-[11px] uppercase tracking-wider transition-all"
                                >
                                  Converter para m²
                                </button>
                                <button
                                  onClick={() => {
                                    setManualConversionIdx(idx);
                                    setManualUnit('m');
                                    setManualCustomUnit('');
                                    setManualYield('');
                                  }}
                                  className="bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 py-2 px-3 rounded-xl font-bold text-[11px] uppercase tracking-wider transition-all whitespace-nowrap"
                                >
                                  Converter Manualmente
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      await supabase.rpc('confirm_unit_conversion_rule', {
                                        p_fornecedor_cnpj: conv.fornecedor_cnpj,
                                        p_fornecedor_nome: conv.fornecedor_nome,
                                        p_codigo_produto: conv.codigo_produto,
                                        p_descricao_snapshot: conv.descricao,
                                        p_unidade_origem: conv.unidade_atual,
                                        p_unidade_convertida: conv.unidade_atual,
                                        p_area_m2_calculada: 0,
                                        p_fator_conversao: 1,
                                        p_action: 'reject',
                                        p_deseja_compartilhar: false,
                                        p_pattern_id: conv.pattern_id
                                      });
                                      setPendingConversions(prev => prev.filter((_, i) => i !== idx));
                                    } catch (err) {
                                      console.error('Erro ao rejeitar conversão:', err);
                                      setPendingConversions(prev => prev.filter((_, i) => i !== idx));
                                    }
                                  }}
                                  className="bg-white border border-zinc-200 text-zinc-500 hover:bg-zinc-100 py-2 px-3 rounded-xl font-bold text-[11px] uppercase tracking-wider transition-all whitespace-nowrap"
                                >
                                  Manter como UN
                                </button>
                              </div>
                            ) : (
                              <div className="bg-white border border-amber-200 rounded-xl p-4 mt-2 shadow-sm animate-in fade-in slide-in-from-top-2">
                                <h5 className="text-[10px] font-black text-amber-900 uppercase tracking-widest mb-3">Conversão Manual</h5>
                                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                                  <div className="flex-1">
                                    <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider mb-1 block">Nova Unidade</label>
                                    <select
                                      value={manualUnit}
                                      onChange={(e) => setManualUnit(e.target.value)}
                                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-sm font-bold text-zinc-800"
                                    >
                                      <option value="m">m (Metros)</option>
                                      <option value="m²">m² (Metros Quadrados)</option>
                                      <option value="kg">kg (Quilogramas)</option>
                                      <option value="g">g (Gramas)</option>
                                      <option value="L">L (Litros)</option>
                                      <option value="ml">ml (Mililitros)</option>
                                      <option value="cx">cx (Caixa)</option>
                                      <option value="outra">Outra...</option>
                                    </select>
                                    {manualUnit === 'outra' && (
                                      <input
                                        type="text"
                                        placeholder="Digite a unidade"
                                        value={manualCustomUnit}
                                        onChange={(e) => setManualCustomUnit(e.target.value)}
                                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-sm font-bold text-zinc-800 mt-2"
                                      />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider mb-1 block">Rendimento</label>
                                    <div className="relative">
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Ex: 2.5"
                                        value={manualYield}
                                        onChange={(e) => setManualYield(e.target.value)}
                                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-2 px-3 text-sm font-bold text-zinc-800 pr-12"
                                      />
                                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-zinc-400">
                                        {manualUnit === 'outra' ? (manualCustomUnit || '...') : manualUnit}
                                      </span>
                                    </div>
                                    <p className="text-[9px] text-zinc-400 mt-1">1 {conv.unidade_atual} equivale a quanto?</p>
                                  </div>
                                </div>

                                {Number(manualYield) > 0 && (
                                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-4 flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                                    <p className="text-xs text-emerald-800">
                                      <strong>1 {conv.unidade_atual}</strong> vai entrar no estoque como <strong>{Number(manualYield)} {manualUnit === 'outra' ? (manualCustomUnit || '...') : manualUnit}</strong>. O sistema fará a divisão do preço automaticamente!
                                    </p>
                                  </div>
                                )}

                                <div className="flex gap-2">
                                  <button
                                    onClick={async () => {
                                      const yieldValue = Number(manualYield);
                                      if (isNaN(yieldValue) || yieldValue <= 0) {
                                        alert("O fator de conversão (rendimento) deve ser maior que zero.");
                                        return;
                                      }

                                      let finalUnit = manualUnit === 'outra' ? manualCustomUnit.trim().toLowerCase() : manualUnit;
                                      if (!finalUnit) {
                                        alert("Por favor, informe a unidade.");
                                        return;
                                      }
                                      
                                      // Normalização de strings comuns para evitar fragmentação na base
                                      const normalizeMap: Record<string, string> = {
                                        'metros': 'm', 'metro': 'm', 'mt': 'm', 'mts': 'm',
                                        'metros quadrados': 'm²', 'm2': 'm²', 'mq': 'm²',
                                        'quilo': 'kg', 'quilos': 'kg', 'kilograma': 'kg', 'kilos': 'kg',
                                        'grama': 'g', 'gramas': 'g', 'gr': 'g',
                                        'litro': 'l', 'litros': 'l', 'lt': 'l', 'lts': 'l'
                                      };
                                      if (normalizeMap[finalUnit]) {
                                        finalUnit = normalizeMap[finalUnit];
                                      }

                                      const checkbox = document.getElementById(`share-conv-${idx}`) as HTMLInputElement;
                                      const desejaCompartilhar = checkbox?.checked || false;

                                      try {
                                        const { error } = await supabase.rpc('confirm_unit_conversion_rule', {
                                          p_fornecedor_cnpj: conv.fornecedor_cnpj,
                                          p_fornecedor_nome: conv.fornecedor_nome,
                                          p_codigo_produto: conv.codigo_produto,
                                          p_descricao_snapshot: conv.descricao,
                                          p_unidade_origem: conv.unidade_atual,
                                          p_unidade_convertida: finalUnit,
                                          p_area_m2_calculada: 0, // 0 pois pode não ser área
                                          p_fator_conversao: yieldValue,
                                          p_action: 'confirm',
                                          p_deseja_compartilhar: desejaCompartilhar,
                                          p_pattern_id: conv.pattern_id // fallback para pattern se for o caso
                                        });

                                        if (error) throw error;

                                        setPendingItems((prev: any) => prev.map((item: any) => {
                                          if (item.name === conv.descricao) {
                                            const currentQty = Number(item.amount || item.quantity || 1);
                                            return {
                                              ...item,
                                              unit: finalUnit,
                                              amount: currentQty * yieldValue,
                                              quantity: currentQty * yieldValue
                                            };
                                          }
                                          return item;
                                        }));

                                        setPendingConversions(prev => prev.filter((_, i) => i !== idx));
                                        setManualConversionIdx(null);
                                        setStatusMessage('Conversão manual salva com sucesso!');
                                        setTimeout(() => setStatusMessage(null), 3000);
                                      } catch (err) {
                                        console.error('Erro ao salvar conversão manual:', err);
                                        alert('Erro ao salvar regra de conversão manual.');
                                      }
                                    }}
                                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2 px-3 rounded-xl font-bold text-[11px] uppercase tracking-wider transition-all"
                                  >
                                    Salvar Regra Personalizada
                                  </button>
                                  <button
                                    onClick={() => setManualConversionIdx(null)}
                                    className="bg-white border border-zinc-200 text-zinc-500 hover:bg-zinc-100 py-2 px-3 rounded-xl font-bold text-[11px] uppercase tracking-wider transition-all"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Validation Queue */}
              {pendingItems.length > 0 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-500">
                  <div className="flex items-center justify-between px-1">
                    <h4 className="text-xs font-black uppercase tracking-[0.15em] text-zinc-700 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-amber-600" /> 
                      Revisar Extração ({pendingItems.length})
                    </h4>
                  </div>
                  
                  {pendingItems.map((item, idx) => {
                    const matchInfo = (item as any).suggestedMatch;
                    return (
                    <div key={item.id} className={`bg-white border-2 border-amber-500/20 rounded-3xl p-5 sm:p-6 shadow-xl relative overflow-hidden transition-all ${idx > 0 ? 'opacity-50 scale-95' : ''}`}>
                      <div className="flex flex-col gap-5">
                        {matchInfo && (
                          <div className="bg-amber-50/90 border border-amber-300 rounded-2xl p-4 text-xs">
                            <div className="flex items-center gap-2 font-bold text-amber-900 mb-1">
                              <Zap className="h-4 w-4 text-amber-600" />
                              <span className="text-sm">Item Parecido Encontrado:</span>
                            </div>
                            <p className="text-amber-900 font-medium mb-3 text-sm">
                              Este material parece ser <strong>"{matchInfo.existingItemName}"</strong> já cadastrado na sua lista.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <button
                                onClick={async () => {
                                  await handleUpdateExistingMaterialPrice(matchInfo.existingItemId, item.price, item.name);
                                  discardItem(item.id);
                                  setStatusMessage(`✓ Preço de "${matchInfo.existingItemName}" atualizado para ${formatCurrencyBRL(item.price)}!`);
                                  setTimeout(() => setStatusMessage(null), 4000);
                                }}
                                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2.5 px-4 rounded-xl font-bold text-xs uppercase tracking-wider transition-all cursor-pointer"
                              >
                                Atualizar Item Existente
                              </button>
                              <button
                                onClick={() => {
                                  updatePendingItem(item.id, { suggestedMatch: undefined } as any);
                                }}
                                className="bg-white hover:bg-zinc-100 text-zinc-800 border border-zinc-300 py-2.5 px-4 rounded-xl font-bold text-xs uppercase tracking-wider transition-all cursor-pointer"
                              >
                                Cadastrar Novo
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-1.5 block">Nome do Material / Insumo</label>
                            <input
                              type="text"
                              value={item.name}
                              onChange={(e) => updatePendingItem(item.id, { name: e.target.value })}
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 px-4 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-1.5 block">Unidade de Medida</label>
                            <input
                              type="text"
                              value={item.unit}
                              onChange={(e) => updatePendingItem(item.id, { unit: e.target.value })}
                              placeholder="ex: un, kg, g, pct, hr"
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 px-4 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-1.5 block">Categoria</label>
                            <select
                              value={item.category}
                              onChange={(e) => updatePendingItem(item.id, { category: e.target.value as 'fixo' | 'variável' })}
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 px-4 text-sm font-bold text-zinc-900 appearance-none capitalize focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                            >
                              <option value="variável">Material / Produto (Variável)</option>
                              <option value="fixo">Fixo (Estrutura)</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide mb-1.5 block">Preço Pago</label>
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-black text-zinc-600">R$</span>
                              <CurrencyInput
                                value={item.price}
                                onChange={(val) => updatePendingItem(item.id, { price: val })}
                                className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl py-3.5 pl-11 pr-4 text-sm font-mono font-black text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                              />
                            </div>
                          </div>
                        </div>

                        {item.category === 'fixo' && (
                          <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Info className="h-4 w-4 text-amber-600" />
                                <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">Diluição de Investimento</span>
                              </div>
                              <span className="text-xs text-amber-700 font-bold uppercase">Bem Durável / Maquinário</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex-1">
                                <label className="text-xs font-bold text-amber-900/60 uppercase mb-1 block">Vida Útil (Meses)</label>
                                <input
                                  type="number"
                                  min="1"
                                  placeholder="Ex: 24"
                                  value={item.depreciationMonths || ''}
                                  onChange={(e) => updatePendingItem(item.id, { depreciationMonths: Number(e.target.value) || undefined })}
                                  className="w-full bg-white border border-amber-200 rounded-xl py-2 px-3 text-sm font-black text-amber-700"
                                />
                              </div>
                              <div className="flex-1 text-right">
                                <p className="text-xs font-bold text-amber-900/60 uppercase mb-1">Impacto Mensal</p>
                                <p className="text-sm font-black text-amber-800">
                                  {item.depreciationMonths && item.depreciationMonths > 0 
                                    ? formatCurrencyBRL(item.price / item.depreciationMonths) 
                                    : formatCurrencyBRL(item.price)}
                                  <span className="text-xs font-medium opacity-75">/mês</span>
                                </p>
                              </div>
                            </div>
                            <p className="mt-3 text-xs text-amber-800/80 leading-relaxed italic">
                              *Isso evita que o valor total do equipamento exploda seu custo de apenas um mês.
                            </p>
                          </div>
                        )}

                        <div className="flex gap-3 pt-1">
                          <button
                            onClick={async () => {
                              if (confirmingId === item.id) return;
                              setConfirmingId(item.id);
                              try {
                                await confirmItem(item.id);
                              } finally {
                                setConfirmingId(null);
                              }
                            }}
                            disabled={confirmingId === item.id}
                            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-3.5 rounded-2xl font-bold text-xs uppercase tracking-wider shadow-md shadow-amber-600/20 active:scale-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2 cursor-pointer"
                          >
                            {confirmingId === item.id ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin text-white" />
                                <span>Salvando...</span>
                              </>
                            ) : (
                              <span>Confirmar Item</span>
                            )}
                          </button>
                          <button
                            onClick={() => discardItem(item.id)}
                            className="bg-zinc-100 hover:bg-rose-50 text-zinc-700 hover:text-rose-600 border border-zinc-200 px-6 rounded-2xl font-bold uppercase text-xs transition-all cursor-pointer"
                          >
                            Descartar
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              )}

              {/* Suggestions Panel — persistent until added or dismissed */}
              {suggestions.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 px-1">
                    <Zap className="h-3 w-3 text-amber-600" />
                    <h3 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Sugeridos para {user.niche}</h3>
                  </div>
                  <div className="flex flex-col gap-2">
                    {suggestions.map((s) => (
                      <div
                        key={s.id}
                        className="flex items-center justify-between bg-white border border-zinc-100 rounded-2xl px-4 py-3 shadow-sm"
                      >
                        <div className="min-w-0">
                          <p className="text-[11px] font-bold text-zinc-700 truncate">{s.name}</p>
                          <p className="text-[9px] text-zinc-400 uppercase tracking-wide mt-0.5">{s.category}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-3 shrink-0">
                          <button
                            onClick={() => handleQuickAddDirect(s)}
                            className="bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-black px-3 py-1.5 rounded-xl transition-colors flex items-center gap-1"
                          >
                            <Plus className="h-3 w-3" />
                            Adicionar
                          </button>
                          <button
                            onClick={() => dismissSuggestion(s.id)}
                            title="Não preciso deste item"
                            className="text-zinc-300 hover:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}


              {/* Inventory List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between px-1 shrink-0">
                  <h3 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">
                    Insumos & Potes de Estoque ({filteredMaterials.length})
                  </h3>
                  <div className="h-px flex-1 bg-zinc-100 mx-4" />
                  <button 
                    onClick={handleCreateNew}
                    className="text-amber-600 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 hover:underline"
                  >
                    <Plus className="h-3 w-3" />
                    Cadastrar Insumo / Pote
                  </button>
                </div>

                {filteredMaterials.length > 0 ? (
                  <div className="space-y-3">
                    {filteredMaterials.map((m) => (
                      <motion.button
                        key={m.id}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        onClick={() => handleEditOpen(m)}
                        className="w-full bg-white rounded-[28px] p-5 shadow-sm border border-zinc-100 flex items-center justify-between hover:border-amber-400/40 transition-all group relative overflow-hidden text-left"
                      >
                        <div className="flex items-center gap-4 relative z-10">
                          <div className={`p-3 rounded-2xl shadow-sm transition-transform group-hover:scale-110 ${
                            m.category === 'fixo' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
                          }`}>
                            {m.category === 'fixo' ? <Home className="h-5 w-5" /> : <Package className="h-5 w-5" />}
                          </div>
                          <div className="text-left">
                            <h4 className="font-bold text-zinc-900 leading-tight">
                              {m.name}
                              {m.brand && (
                                <span className="text-zinc-400 font-medium text-xs ml-1.5">
                                  ({m.brand})
                                </span>
                              )}
                            </h4>
                            <p className="text-[9px] font-black uppercase tracking-widest text-zinc-400 mt-1 flex items-center gap-1.5">
                              {m.unit} 
                              <span className="w-1 h-1 rounded-full bg-zinc-200" />
                              <span className={m.category === 'fixo' ? 'text-amber-500' : 'text-emerald-500'}>
                                {resolveStockControlMode(m) === 'unit_sale' ? 'Água / Picolé / Unit.' : 'Pote / Insumo'}
                              </span>
                              {m.supplierName && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-zinc-200" />
                                  <span className="text-zinc-600">{m.supplierName}</span>
                                </>
                              )}
                              {m.barcode && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-zinc-200" />
                                  <span className="font-mono text-zinc-500">{m.barcode}</span>
                                </>
                              )}
                              {m.category === 'fixo' && m.depreciationMonths && m.depreciationMonths > 1 && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-zinc-200" />
                                  <span className="text-amber-700 italic">Diluído em {m.depreciationMonths}x</span>
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="text-right relative z-10 flex items-center gap-3">
                          <div className="hidden sm:flex items-center gap-1.5">
                            <span className="px-2.5 py-1 rounded-xl bg-zinc-100 text-zinc-700 text-[10px] font-black font-mono">
                              Est: {Number(m.stockQuantity || 0)} {m.unit || 'un'}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => handleQuickDepleteMaterial(m, -1, e)}
                              className="px-2.5 py-1.5 rounded-xl bg-amber-50 hover:bg-amber-500 text-amber-800 hover:text-white border border-amber-200 text-[10px] font-black uppercase tracking-wider transition-colors flex items-center gap-1"
                              title="Dar baixa de 1 pote/unidade no estoque"
                            >
                              <ArrowDownRight className="w-3 h-3" />
                              Dar Baixa
                            </button>
                          </div>
                          <div>
                            <p className="font-mono font-black text-zinc-900 text-lg">
                              {formatCurrencyBRL(m.pricePaid)}
                            </p>
                            <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-tighter">
                              {m.depreciationMonths && m.depreciationMonths > 0 
                                ? `${formatCurrencyBRL(m.pricePaid / m.depreciationMonths)}/mês` 
                                : `por ${m.yieldAmount} ${m.unit}`}
                            </p>
                          </div>
                        </div>
                        
                        {/* Decorative background element for category */}
                        <div className={`absolute right-0 top-0 w-24 h-24 -mr-8 -mt-8 rounded-full opacity-[0.03] transition-all group-hover:scale-150 ${
                          m.category === 'fixo' ? 'bg-amber-600' : 'bg-emerald-600'
                        }`} />
                      </motion.button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 bg-white rounded-4xl border-2 border-dashed border-zinc-100">
                    <div className="bg-zinc-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="h-8 w-8 text-zinc-300" />
                    </div>
                    <h3 className="font-black text-zinc-900 uppercase text-xs tracking-widest mb-1">
                      {materials.length === 0 ? 'Inventário Vazio' : 'Nenhum item encontrado'}
                    </h3>
                    <p className="text-xs text-zinc-400 max-w-xs mx-auto">
                      {materials.length === 0
                        ? 'Use o R-Max acima para começar a registrar seus custos fixos e variáveis.'
                        : 'Tente buscar por outro termo ou alternar o filtro de categoria.'}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {mainTab === 'products' && (
            <motion.div
              key="products"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="h-full min-h-0 overflow-y-auto w-full"
            >
              <ProductCatalogManager
                user={user}
                config={config}
                materials={materials}
                onSaveMaterials={onSaveMaterials}
                onBack={() => changeTab('materials')}
                onNavigateToStock={onNavigateToStock}
              />
            </motion.div>
          )}

          {mainTab === 'recipes' && (
            <motion.div
              key="recipes"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="h-full min-h-0 overflow-y-auto w-full"
            >
              {user.plan === 'Grátis' ? (
                <PaywallEmptyState 
                  title="Receitas e Kits" 
                  description="A organização inteligente de múltiplas receitas e kits de produtos é uma funcionalidade do Plano PRO. Ideal para padronizar orçamentos complexos com agilidade e consistência operacional."
                  onUpgrade={() => setIsPaywallOpen(true)}
                />
              ) : (
                <RecipeManager
                  user={user}
                  config={config}
                  materials={materials}
                  recipes={recipes}
                  onSaveRecipe={onSaveRecipe}
                  onSaveMaterials={handleSyncMaterials}
                  onDeleteRecipe={onDeleteRecipe}
                  onBack={() => changeTab('materials')}
                />
              )}
            </motion.div>
          )}

          {mainTab === 'services' && (
            <motion.div
              key="services"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="h-full min-h-0 overflow-y-auto w-full"
            >
              {user.plan === 'Grátis' ? (
                <PaywallEmptyState 
                  title="Catálogo de Serviços" 
                  description="O cadastro de serviços fixos e precificação por hora de máquina/ferramenta é parte do Plano PRO. Simplifique a montagem de orçamentos recorrentes!"
                  onUpgrade={() => setIsPaywallOpen(true)}
                />
              ) : (
                <ServiceManager
                  user={user}
                  config={config}
                  services={services}
                  onSaveService={onSaveService}
                  onDeleteService={onDeleteService}
                  onBack={() => changeTab('materials')}
                />
              )}
            </motion.div>
          )}
          {mainTab === 'fixed_costs' && (
            <motion.div
              key="fixed_costs"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="h-full min-h-0 overflow-y-auto w-full pb-32 px-4 pt-6 space-y-6"
            >
              <div className="bg-amber-50/80 border border-amber-200 rounded-3xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-3 bg-amber-500 text-white rounded-2xl shadow-sm shrink-0">
                    <Home className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-amber-950 uppercase tracking-wider">
                      Despesas Fixas & Folha de Pagamento
                    </h3>
                    <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                      Folha salarial, aluguel, energia, internet e depreciação de maquinário ficam separados aqui e não poluem a tela de Estoque Físico.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCreateNewFixedExpense}
                  className="px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-md shrink-0 cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  Nova Despesa / Folha
                </button>
              </div>

              <div className="bg-white border border-zinc-100 p-4 rounded-3xl shadow-sm">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                  <input
                    type="text"
                    value={fixedSearchTerm}
                    onChange={(e) => setFixedSearchTerm(e.target.value)}
                    placeholder="Pesquisar despesa fixa ou folha de pagamento..."
                    className="w-full bg-zinc-50 border border-zinc-200/80 rounded-2xl py-3 pl-11 pr-10 text-xs font-bold text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>
              </div>

              <div className="space-y-3">
                {filteredFixedExpenses.length > 0 ? (
                  filteredFixedExpenses.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => handleEditOpen(m)}
                      className="w-full bg-white rounded-[28px] p-5 shadow-sm border border-zinc-100 flex items-center justify-between hover:border-amber-400/40 transition-all text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-amber-50 text-amber-600">
                          <Home className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-bold text-zinc-900 leading-tight">{m.name}</h4>
                          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mt-1">
                            Despesa Fixa / Estrutura • {m.unit || 'mês'}
                            {m.depreciationMonths && m.depreciationMonths > 1 ? ` • Diluído em ${m.depreciationMonths}x` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-black text-zinc-900 text-lg">
                          {formatCurrencyBRL(m.pricePaid)}
                        </p>
                        <p className="text-[10px] text-zinc-400 font-bold uppercase">
                          {m.depreciationMonths && m.depreciationMonths > 0
                            ? `${formatCurrencyBRL(m.pricePaid / m.depreciationMonths)}/mês`
                            : 'Custo Mensal'}
                        </p>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-14 bg-white rounded-3xl border-2 border-dashed border-zinc-100">
                    <Home className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
                    <p className="text-xs font-black text-zinc-800 uppercase tracking-wider">
                      Nenhuma despesa fixa ou folha cadastrada
                    </p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Clique em "Nova Despesa / Folha" para registrar salários, aluguel, contador ou contas fixas.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Edit / New Material Modal */}
      <AnimatePresence>
        {isEditModalOpen && editingMaterial && (
          <div className="fixed inset-0 z-100 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
              onClick={() => setIsEditModalOpen(false)}
              className="absolute inset-0 bg-zinc-900/80 backdrop-blur-sm" 
            />
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              className="relative w-full max-w-lg bg-white rounded-t-4xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] sm:max-h-[85vh] z-10"
            >
              {/* Header */}
              <div className="p-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/50 shrink-0">
                <div>
                  <h3 className="font-black text-zinc-900 uppercase tracking-widest text-xs">
                    {editingMaterial.id ? 'Editar Item' : 'Cadastrar Item Manualmente'}
                  </h3>
                  <p className="text-xs text-zinc-500">Defina o nome e valor do material ou equipamento.</p>
                </div>
                <button onClick={() => setIsEditModalOpen(false)} className="bg-zinc-200/50 hover:bg-zinc-200 p-2 rounded-full transition-colors">
                  <ArrowLeft className="h-5 w-5 text-zinc-600 rotate-180" />
                </button>
              </div>

              {/* Scrollable Form Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                <div>
                  <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2 block px-2">Nome do Material/Custo</label>
                  <input
                    type="text"
                    value={editingMaterial.name}
                    onChange={(e) => setEditingMaterial({ ...editingMaterial, name: e.target.value })}
                    className="w-full bg-zinc-50/50 border border-zinc-200 rounded-2xl py-4 px-5 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all placeholder:text-zinc-300"
                    placeholder="Nome do item"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2 block px-2">Marca (Opcional)</label>
                  <input
                    type="text"
                    value={editingMaterial.brand || ''}
                    onChange={(e) => setEditingMaterial({ ...editingMaterial, brand: e.target.value })}
                    className="w-full bg-zinc-50/50 border border-zinc-200 rounded-2xl py-4 px-5 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all placeholder:text-zinc-300"
                    placeholder="Ex: Dona Benta, Nestlé"
                  />
                </div>

                {editingMaterial.category !== 'fixo' ? (
                  <div className="space-y-4 bg-emerald-50/40 border border-emerald-200/70 rounded-2xl p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-emerald-900 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                          <Truck className="w-3.5 h-3.5 text-emerald-600" />
                          Nome do Fornecedor
                        </label>
                        <input
                          type="text"
                          value={editingMaterial.supplierName || ''}
                          onChange={(e) => setEditingMaterial({ ...editingMaterial, supplierName: e.target.value })}
                          className="w-full bg-white border border-emerald-200 rounded-xl py-3 px-4 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          placeholder="Ex: Kibon, Nestlé, Distribuidora"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-emerald-900 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                          <Barcode className="w-3.5 h-3.5 text-emerald-600" />
                          Código de Barras (EAN)
                        </label>
                        <input
                          type="text"
                          data-barcode-input="true"
                          value={editingMaterial.barcode || ''}
                          onChange={(e) => setEditingMaterial({ ...editingMaterial, barcode: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                          className="w-full bg-white border border-emerald-200 rounded-xl py-3 px-4 text-sm font-bold font-mono text-zinc-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          placeholder="Bipe com a leitora externa..."
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-emerald-900 uppercase tracking-widest mb-1.5 block">
                          Modo de Controle no Estoque
                        </label>
                        <select
                          value={editingMaterial.stockControlMode || resolveStockControlMode(editingMaterial)}
                          onChange={(e) => setEditingMaterial({ ...editingMaterial, stockControlMode: e.target.value as 'pot_open' | 'unit_sale' })}
                          className="w-full bg-white border border-emerald-200 rounded-xl py-3 px-3 text-xs font-bold text-zinc-900"
                        >
                          <option value="pot_open">Pote / Caixa Buffet (Baixa ao Abrir)</option>
                          <option value="unit_sale">Água / Picolé / Unitário (Fardo ou Un.)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-emerald-900 uppercase tracking-widest mb-1.5 block">
                          Saldo em Estoque & Baixa Rápida
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            step="any"
                            value={editingMaterial.stockQuantity ?? 0}
                            onChange={(e) => setEditingMaterial({ ...editingMaterial, stockQuantity: Math.max(0, Number(e.target.value)) })}
                            className="w-full bg-white border border-emerald-200 rounded-xl py-3 px-3 text-sm font-black font-mono text-zinc-900"
                          />
                          <button
                            type="button"
                            onClick={() => setEditingMaterial({
                              ...editingMaterial,
                              stockQuantity: Math.max(0, Number(((editingMaterial.stockQuantity || 0) - 1).toFixed(3)))
                            })}
                            className="px-3 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black uppercase tracking-wider shrink-0 transition-colors"
                            title="Dar baixa de 1 pote ou unidade"
                          >
                            -1 Baixa
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2 block px-2">Categoria</label>
                    <div className="relative">
                      <select
                        value={editingMaterial.category}
                        onChange={(e) => setEditingMaterial({ ...editingMaterial, category: e.target.value as 'fixo' | 'variável' })}
                        className="w-full bg-zinc-50/50 border border-zinc-200 rounded-2xl py-4 pl-5 pr-12 text-sm font-bold appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all capitalize text-zinc-900 cursor-pointer"
                      >
                        <option value="variável">Material / Produto (Variável)</option>
                        <option value="fixo">Fixo (Base)</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <ChevronDown className="h-5 w-5 text-zinc-400" />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2 block px-2">Valor Total Gasto (R$)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-black text-zinc-400">R$</span>
                      <CurrencyInput
                        value={editingMaterial.pricePaid || 0}
                        onChange={(val) => setEditingMaterial({ ...editingMaterial, pricePaid: val })}
                        className="w-full bg-zinc-50/50 border border-zinc-200 rounded-2xl py-4 pl-11 pr-4 text-sm font-mono font-black text-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all cursor-text"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-2 block px-2">Rende Quanto?</label>
                  <div className="flex gap-3">
                    <input
                      type="number"
                      value={editingMaterial.yieldAmount || ''}
                      onChange={(e) => setEditingMaterial({ ...editingMaterial, yieldAmount: Number(e.target.value) })}
                      className="w-1/2 bg-zinc-50/50 border border-zinc-200 rounded-2xl py-4 px-5 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all"
                      placeholder="Qtd"
                    />
                    <input
                      type="text"
                      value={editingMaterial.unit || ''}
                      onChange={(e) => setEditingMaterial({ ...editingMaterial, unit: e.target.value })}
                      className="w-1/2 bg-zinc-50/50 border border-zinc-200 rounded-2xl py-4 px-5 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all"
                      placeholder="Unidade (ex: kg, gr, un)"
                    />
                  </div>
                </div>

                {editingMaterial.category === 'fixo' && (
                  <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100">
                    <div className="flex items-center gap-2 mb-4">
                      <Home className="h-5 w-5 text-amber-600" />
                      <h4 className="text-xs font-black text-amber-900 uppercase tracking-widest">Configuração de Ativo</h4>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="text-[10px] font-black text-amber-800 uppercase tracking-widest mb-2 block">Vida Útil Estimada (Meses)</label>
                        <input
                          type="number"
                          min="1"
                          value={editingMaterial.depreciationMonths || ''}
                          onChange={(e) => setEditingMaterial({ ...editingMaterial, depreciationMonths: Number(e.target.value) })}
                          className="w-full bg-white border border-amber-200 rounded-xl py-3 px-4 text-sm font-black text-amber-600 focus:ring-2 focus:ring-amber-500/20"
                          placeholder="Ex: 24 meses"
                        />
                        <p className="text-[10px] text-amber-700/60 mt-2 italic">
                          Deixe vazio se for um custo fixo recorrente (ex: Aluguel).
                        </p>
                      </div>
                      {editingMaterial.depreciationMonths && editingMaterial.depreciationMonths > 0 && (
                        <div className="flex justify-between items-center pt-2 border-t border-amber-100">
                          <span className="text-[10px] font-bold text-amber-800 uppercase">Impacto Mensal:</span>
                          <span className="text-sm font-black text-amber-600">
                            {formatCurrencyBRL(editingMaterial.pricePaid / editingMaterial.depreciationMonths)}/mês
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sticky Action Footer */}
              <div className="p-6 bg-zinc-50 border-t border-zinc-100 flex flex-col gap-3 shrink-0">
                <button
                  onClick={handleUpdateMaterial}
                  className="w-full bg-amber-500 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-amber-500/20 active:scale-95 transition-all"
                >
                  {editingMaterial.id ? 'Atualizar Dados' : 'Salvar Item'}
                </button>
                {editingMaterial.id && (
                  <button
                    onClick={() => handleRemoveMaterial(editingMaterial.id!)}
                    className="w-full py-3.5 text-rose-500 font-bold text-xs uppercase tracking-widest hover:bg-rose-50 rounded-2xl transition-all"
                  >
                    Remover do Sistema
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de confirmação de exclusão quando insumo está em uso por receitas */}
      <AnimatePresence>
        {pendingDeleteMaterial && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-100 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-md rounded-3xl p-6 shadow-2xl space-y-5 border border-zinc-100"
            >
              <div className="flex items-center gap-3 text-amber-600">
                <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-extrabold text-zinc-900 text-lg">Material em Uso</h3>
                  <p className="text-xs font-medium text-zinc-500">Este item está em receitas ou kits</p>
                </div>
              </div>

              <div className="bg-amber-50/70 rounded-2xl p-4 border border-amber-200/60 space-y-3">
                <p className="text-xs text-amber-900 leading-relaxed">
                  O material <strong className="font-bold text-amber-950">"{pendingDeleteMaterial.name}"</strong> está presente nas seguintes receitas:
                </p>
                <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
                  {linkedRecipesForDelete.map((recipe) => (
                    <div key={recipe.id} className="flex items-center gap-2 text-xs font-semibold text-amber-950 bg-white/80 px-3 py-2 rounded-xl border border-amber-200/50">
                      <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0"></span>
                      <span className="truncate">{recipe.name}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] font-medium text-amber-800 italic">
                  Se você excluir mesmo assim, ele será removido automaticamente dessas receitas.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={cancelDeleteMaterial}
                  className="w-full sm:flex-1 py-3 bg-zinc-100 text-zinc-700 font-bold text-xs uppercase tracking-wider rounded-2xl hover:bg-zinc-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => confirmDeleteMaterial(pendingDeleteMaterial.id!)}
                  className="w-full sm:flex-1 py-3 bg-rose-600 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-rose-600/20 hover:bg-rose-700 transition-colors"
                >
                  Excluir Mesmo Assim
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ImageCaptureMenu
        isOpen={isCaptureMenuOpen}
        onClose={() => setIsCaptureMenuOpen(false)}
        onSelectCamera={() => cameraInputRef.current?.click()}
        onSelectGallery={() => fileInputRef.current?.click()}
        onSelectPdf={() => pdfInputRef.current?.click()}
        title="Capturar Cupom / Custo"
        subtitle="Selecione a origem da imagem ou documento"
      />

      <PaywallModal isOpen={isPaywallOpen} onClose={() => setIsPaywallOpen(false)} />
    </div>
  );
}

interface ImageCaptureMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCamera: () => void;
  onSelectGallery: () => void;
  onSelectPdf?: () => void;
  title?: string;
  subtitle?: string;
}

export function ImageCaptureMenu({ 
  isOpen, 
  onClose, 
  onSelectCamera, 
  onSelectGallery, 
  onSelectPdf,
  title = "Capturar Imagem", 
  subtitle = "Selecione uma opção para continuar" 
}: ImageCaptureMenuProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-150 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-950/60 backdrop-blur-xs"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 350 }}
            className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-zinc-100 overflow-hidden pb-8 sm:pb-6 z-10"
          >
            {/* Header / Indicator for mobile */}
            <div className="flex flex-col items-center pt-3 pb-5 px-6 border-b border-zinc-100">
              <div className="w-12 h-1 bg-zinc-200 rounded-full mb-4 sm:hidden" />
              <h3 className="font-black text-zinc-900 uppercase text-xs tracking-widest">{title}</h3>
              <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-tight mt-1">{subtitle}</p>
            </div>
            
            <div className="p-4 space-y-2">
              <button
                onClick={() => {
                  onSelectCamera();
                  onClose();
                }}
                className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-zinc-50 active:scale-98 transition-all text-left group"
              >
                <div className="bg-amber-500/10 p-3 rounded-xl group-hover:bg-amber-500/20 transition-colors">
                  <Camera className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-zinc-900">Tirar Foto / Escanear</p>
                  <p className="text-xs text-zinc-400">Use a câmera do seu dispositivo</p>
                </div>
              </button>

              <button
                onClick={() => {
                  onSelectGallery();
                  onClose();
                }}
                className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-zinc-50 active:scale-98 transition-all text-left group"
              >
                <div className="bg-amber-500/10 p-3 rounded-xl group-hover:bg-amber-500/20 transition-colors">
                  <ImageIcon className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-zinc-900">Escolher da Galeria</p>
                  <p className="text-xs text-zinc-400">Selecione uma imagem salva</p>
                </div>
              </button>

              {onSelectPdf && (
                <button
                  onClick={() => {
                    onSelectPdf();
                    onClose();
                  }}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-zinc-50 active:scale-98 transition-all text-left group"
                >
                  <div className="bg-amber-500/10 p-3 rounded-xl group-hover:bg-amber-500/20 transition-colors">
                    <FileText className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-zinc-900">Importar PDF (Nota / Orçamento)</p>
                    <p className="text-xs text-zinc-400">Leitura direta de notas fiscais em PDF</p>
                  </div>
                </button>
              )}

              <button
                onClick={onClose}
                className="w-full mt-2 py-3.5 bg-zinc-50 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-2xl font-black text-[11px] uppercase tracking-wider text-center transition-all active:scale-98"
              >
                Cancelar
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

